import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button-tw";
import { Input } from "@/components/ui/input";
import { Copy } from "lucide-react";
import { toast } from "react-toastify";
import {
  useGetCheckoutLink,
  useCreateCheckoutLink,
  useDeleteCheckoutLink,
  checkoutQueryKeys,
} from "@/queries/checkout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface QuickPayLinkSectionProps {
  userId: string;
  isAdmin: boolean;
}

export const QuickPayLinkSection = ({
  userId,
  isAdmin,
}: QuickPayLinkSectionProps) => {
  const queryClient = useQueryClient();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { data: checkoutLink } = useGetCheckoutLink(
    { userId },
    { enabled: !!userId }
  );

  const deleteCheckoutLinkMutation = useDeleteCheckoutLink({
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: checkoutQueryKeys.links(userId),
        });
      }
    },
    onError: (error) => {
      toast.error("Failed to delete old checkout link. Please try again.");
      console.error("Error deleting checkout link:", error);
    },
  });

  const createCheckoutLinkMutation = useCreateCheckoutLink({
    onSuccess: (data) => {
      if (userId) {
        queryClient.setQueryData(checkoutQueryKeys.links(userId), data.link);
        queryClient.invalidateQueries({
          queryKey: checkoutQueryKeys.links(userId),
        });
      }
      toast.success("Checkout link created successfully!");
    },
    onError: (error) => {
      toast.error("Failed to create checkout link. Please try again.");
      console.error("Error creating checkout link:", error);
    },
  });

  const handleCreateCheckoutLink = () => {
    if (checkoutLink) {
      setShowConfirmDialog(true);
    } else {
      handleConfirmCreateLink();
    }
  };

  const handleConfirmCreateLink = async () => {
    if (!userId) return;

    try {
      if (checkoutLink) {
        await deleteCheckoutLinkMutation.mutateAsync({ userId });
      }

      createCheckoutLinkMutation.mutate({
        userId,
        payload: {
          is_static_link: true,
        },
      });
    } catch (error) {
      console.error("Error in checkout link flow:", error);
    } finally {
      setShowConfirmDialog(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Pay Shareable Link
        </h3>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Send this link directly to your customer or paste it in an email.
            Payments made through this link are processed securely and appear as
            Quick Pays under Invoices.
          </p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type="text"
                readOnly
                value={
                  checkoutLink ||
                  "Click 'Create New' to generate a shareable link"
                }
                className="bg-gray-50 pr-10"
                placeholder="Click 'Create New' to generate a shareable link"
              />
              {checkoutLink && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(checkoutLink);
                    toast.success("Link copied to clipboard!");
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-white hover:bg-gray-100 rounded border border-gray-300 shadow-sm transition-colors"
                >
                  <Copy size={16} className="text-gray-700" />
                </button>
              )}
            </div>
            {!isAdmin && (
              <Button
                variant="outline"
                onClick={handleCreateCheckoutLink}
                disabled={createCheckoutLinkMutation.isPending}
              >
                {createCheckoutLinkMutation.isPending
                  ? "Creating..."
                  : "Create New"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {checkoutLink
                ? "Creating a new shareable link will deactivate the existing one. Make sure to update any places where the previous link is being used."
                : "This will create a new link that you can share with your customers. Are you sure you want to continue?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCreateLink}
              disabled={
                deleteCheckoutLinkMutation.isPending ||
                createCheckoutLinkMutation.isPending
              }
            >
              {deleteCheckoutLinkMutation.isPending ||
              createCheckoutLinkMutation.isPending
                ? "Processing..."
                : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
